import {
  AllSelection,
  NodeSelection,
  Selection,
  TextSelection,
} from "@tiptap/pm/state"
import { cellAround, CellSelection } from "@tiptap/pm/tables"
import { findParentNodeClosestToPos } from "@tiptap/react";

export const MAX_FILE_SIZE = 5 * 1024 * 1024 // 5MB

export const MAC_SYMBOLS = {
  mod: "⌘",
  command: "⌘",
  meta: "⌘",
  ctrl: "⌃",
  control: "⌃",
  alt: "⌥",
  option: "⌥",
  shift: "⇧",
  backspace: "Del",
  delete: "⌦",
  enter: "⏎",
  escape: "⎋",
  capslock: "⇪"
}

export const WINDOWS_SYMBOLS = {
  mod: "Ctrl",
  command: "Ctrl",
  ctrl: "Ctrl",
  control: "Ctrl",
  meta: "Win",
  alt: "Alt",
  option: "Alt",
  shift: "Shift",
  backspace: "Backspace",
  delete: "Delete",
  enter: "Enter",
  escape: "Esc",
  capslock: "Caps Lock"
}

export const SR_ONLY = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  borderWidth: 0
}

export function cn(...classes) {
  return classes.filter(Boolean).join(" ");
}

/**
 * Determines if the current platform is macOS
 * @returns boolean indicating if the current platform is Mac
 */
export function isMac() {
  return (typeof navigator !== "undefined" && navigator.platform.toLowerCase().includes("mac"));
}

/**
 * Formats a shortcut key based on the platform (Mac or non-Mac)
 * @param key - The key to format (e.g., "ctrl", "alt", "shift")
 * @param isMac - Boolean indicating if the platform is Mac
 * @param capitalize - Whether to capitalize the key (default: true)
 * @returns Formatted shortcut key symbol
 */
export const formatShortcutKey = (
  key,
  isMac,
  capitalize = true
) => {
  const lowerKey = key.toLowerCase()

  if (isMac) {
    return MAC_SYMBOLS[lowerKey] || (capitalize ? key.toUpperCase() : key);
  }

  if (WINDOWS_SYMBOLS[lowerKey]) {
    return WINDOWS_SYMBOLS[lowerKey];
  }

  return capitalize ? key.charAt(0).toUpperCase() + key.slice(1) : key;
}

/**
 * Parses a shortcut key string into an array of formatted key symbols
 * @param shortcutKeys - The string of shortcut keys (e.g., "ctrl-alt-shift")
 * @param delimiter - The delimiter used to split the keys (default: "-")
 * @param capitalize - Whether to capitalize the keys (default: true)
 * @returns Array of formatted shortcut key symbols
 */
export const parseShortcutKeys = (props) => {
  const { shortcutKeys, delimiter = "+", capitalize = true } = props

  if (!shortcutKeys) return []

  return shortcutKeys
    .split(delimiter)
    .map((key) => key.trim())
    .map((key) => formatShortcutKey(key, isMac(), capitalize));
}

/**
 * Checks if a mark exists in the editor schema
 * @param markName - The name of the mark to check
 * @param editor - The editor instance
 * @returns boolean indicating if the mark exists in the schema
 */
export const isMarkInSchema = (markName, editor) => {
  if (!editor?.schema) return false
  return editor.schema.spec.marks.get(markName) !== undefined;
}

/**
 * Checks if a node exists in the editor schema
 * @param nodeName - The name of the node to check
 * @param editor - The editor instance
 * @returns boolean indicating if the node exists in the schema
 */
export const isNodeInSchema = (nodeName, editor) => {
  if (!editor?.schema) return false
  return editor.schema.spec.nodes.get(nodeName) !== undefined;
}

/**
 * TipTap focus options that skip ProseMirror's scroll-into-view.
 * Pair with {@link runWithPreservedWindowScroll} for toolbar actions on mobile —
 * TipTap still calls `view.dom.focus()` without `preventScroll` on iOS/Android.
 */
export const FOCUS_NO_SCROLL = { scrollIntoView: false }

/**
 * Run an editor UI action without jumping the page on touch devices.
 *
 * TipTap's `focus()` on iOS/Android calls `view.dom.focus()` without
 * `preventScroll`, which scrolls the contenteditable into view (usually the
 * top of the editor). `handleScrollToSelection` already suppresses ProseMirror's
 * own scroll-to-selection on coarse pointers; this restores window scroll after
 * TipTap's delayed focus `requestAnimationFrame`.
 *
 * @param {() => T} fn
 * @returns {T}
 * @template T
 */
export function runWithPreservedWindowScroll(fn) {
  if (typeof window === "undefined") return fn()

  const { scrollX, scrollY } = window
  const result = fn()

  const restore = () => {
    if (window.scrollX !== scrollX || window.scrollY !== scrollY) {
      window.scrollTo(scrollX, scrollY)
    }
  }

  requestAnimationFrame(() => {
    restore()
    requestAnimationFrame(restore)
  })

  return result
}

/**
 * Moves the focus to the next node in the editor
 * @param editor - The editor instance
 * @returns boolean indicating if the focus was moved
 */
export function focusNextNode(editor) {
  const { state, view } = editor
  const { doc, selection } = state

  const nextSel = Selection.findFrom(selection.$to, 1, true)
  if (nextSel) {
    view.dispatch(state.tr.setSelection(nextSel).scrollIntoView())
    return true
  }

  const paragraphType = state.schema.nodes.paragraph
  if (!paragraphType) {
    console.warn("No paragraph node type found in schema.")
    return false
  }

  const end = doc.content.size
  const para = paragraphType.create()
  let tr = state.tr.insert(end, para)

  // Place the selection inside the new paragraph
  const $inside = tr.doc.resolve(end + 1)
  tr = tr.setSelection(TextSelection.near($inside)).scrollIntoView()
  view.dispatch(tr)
  return true
}

/**
 * Checks if a value is a valid number (not null, undefined, or NaN)
 * @param value - The value to check
 * @returns boolean indicating if the value is a valid number
 */
export function isValidPosition(pos) {
  return typeof pos === "number" && pos >= 0
}

/**
 * Checks if one or more extensions are registered in the Tiptap editor.
 * @param editor - The Tiptap editor instance
 * @param extensionNames - A single extension name or an array of names to check
 * @returns True if at least one of the extensions is available, false otherwise
 */
export function isExtensionAvailable(editor, extensionNames) {
  if (!editor) return false

  const names = Array.isArray(extensionNames)
    ? extensionNames
    : [extensionNames]

  const found = names.some((name) =>
    editor.extensionManager.extensions.some((ext) => ext.name === name))

  if (!found) {
    console.warn(
      `None of the extensions [${names.join(", ")}] were found in the editor schema. Ensure they are included in the editor configuration.`
    )
  }

  return found
}

/**
 * Finds a node at the specified position with error handling
 * @param editor The Tiptap editor instance
 * @param position The position in the document to find the node
 * @returns The node at the specified position, or null if not found
 */
export function findNodeAtPosition(editor, position) {
  try {
    const node = editor.state.doc.nodeAt(position)
    if (!node) {
      console.warn(`No node found at position ${position}`)
      return null
    }
    return node
  } catch (error) {
    console.error(`Error getting node at position ${position}:`, error)
    return null
  }
}

/**
 * Finds the position and instance of a node in the document
 * @param props Object containing editor, node (optional), and nodePos (optional)
 * @param props.editor The Tiptap editor instance
 * @param props.node The node to find (optional if nodePos is provided)
 * @param props.nodePos The position of the node to find (optional if node is provided)
 * @returns An object with the position and node, or null if not found
 */
export function findNodePosition(props) {
  const { editor, node, nodePos } = props

  if (!editor || !editor.state?.doc) return null

  // Zero is valid position
  const hasValidNode = node !== undefined && node !== null
  const hasValidPos = isValidPosition(nodePos)

  if (!hasValidNode && !hasValidPos) {
    return null
  }

  // First search for the node in the document if we have a node
  if (hasValidNode) {
    let foundPos = -1
    let foundNode = null

    editor.state.doc.descendants((currentNode, pos) => {
      // TODO: Needed?
      // if (currentNode.type && currentNode.type.name === node!.type.name) {
      if (currentNode === node) {
        foundPos = pos
        foundNode = currentNode
        return false
      }
      return true
    })

    if (foundPos !== -1 && foundNode !== null) {
      return { pos: foundPos, node: foundNode }
    }
  }

  // If we have a valid position, use findNodeAtPosition
  if (hasValidPos) {
    const nodeAtPos = findNodeAtPosition(editor, nodePos)
    if (nodeAtPos) {
      return { pos: nodePos, node: nodeAtPos };
    }
  }

  return null
}

/**
 * Determines whether the current selection contains a node whose type matches
 * any of the provided node type names.
 * @param editor Tiptap editor instance
 * @param nodeTypeNames List of node type names to match against
 * @param checkAncestorNodes Whether to check ancestor node types up the depth chain
 */
export function isNodeTypeSelected(editor, nodeTypeNames = [], checkAncestorNodes = false) {
  if (!editor || !editor.state.selection) return false

  const { selection } = editor.state
  if (selection.empty) return false

  // Direct node selection check
  if (selection instanceof NodeSelection) {
    const selectedNode = selection.node
    return selectedNode ? nodeTypeNames.includes(selectedNode.type.name) : false;
  }

  // Depth-based ancestor node check
  if (checkAncestorNodes) {
    const { $from } = selection
    for (let depth = $from.depth; depth > 0; depth--) {
      const ancestorNode = $from.node(depth)
      if (nodeTypeNames.includes(ancestorNode.type.name)) {
        return true
      }
    }
  }

  return false
}

/**
 * Finds the nearest node of the given type around the current selection.
 * Checks a direct NodeSelection first, then walks ancestor depths.
 * @returns {{ node: object, pos: number } | null}
 */
export function findNodeAroundSelection(state, typeName) {
  if (!state?.selection || !typeName) return null

  const { selection } = state
  if (selection.node?.type.name === typeName) {
    return { node: selection.node, pos: selection.from }
  }

  const { $from } = selection
  for (let depth = $from.depth; depth > 0; depth--) {
    const node = $from.node(depth)
    if (node.type.name === typeName) {
      return { node, pos: $from.before(depth) }
    }
  }

  return null
}

/**
 * Check whether the current selection is fully within nodes
 * whose type names are in the provided `types` list.
 *
 * - NodeSelection → checks the selected node.
 * - Text/AllSelection → ensures all textblocks within [from, to) are allowed.
 */
export function selectionWithinConvertibleTypes(editor, types = []) {
  if (!editor || types.length === 0) return false

  const { state } = editor
  const { selection } = state
  const allowed = new Set(types)

  if (selection instanceof NodeSelection) {
    const nodeType = selection.node?.type?.name
    return !!nodeType && allowed.has(nodeType);
  }

  if (selection instanceof TextSelection || selection instanceof AllSelection) {
    let valid = true
    state.doc.nodesBetween(selection.from, selection.to, (node) => {
      if (node.isTextblock && !allowed.has(node.type.name)) {
        valid = false
        return false // stop early
      }
      return valid
    })
    return valid
  }

  return false
}

const CONVERTIBLE_TYPES = new Set([
  "paragraph", "heading", "bulletList", "orderedList",
  "taskList", "blockquote", "codeBlock",
])

/**
 * Finds the nearest ancestor node whose type is in the convertible set.
 * Falls back to depth 1 if no convertible ancestor is found.
 */
export function findConvertibleAncestor(selection) {
  const { $anchor } = selection

  for (let d = $anchor.depth; d >= 1; d--) {
    const node = $anchor.node(d)
    if (CONVERTIBLE_TYPES.has(node.type.name)) {
      return node
    }
  }

  return $anchor.node(1)
}

/**
 * Builds the upload endpoint for inline images. The photo is tagged with the
 * record it belongs to so the resulting PublicPhoto can be authorized later.
 * With a record type but no id yet (a brand-new record), we send record_type
 * alone so the photo fails closed rather than joining the publicly served
 * untagged bucket. Only when no record type is known do we fall through to an
 * untagged upload.
 */
export function buildImagesUploadUrl(recordContext) {
  const base = "/public_photos"
  const recordType = recordContext?.recordType
  const recordId = recordContext?.recordId

  if (!recordType) {
    return base
  }

  const params = new URLSearchParams({ record_type: recordType })
  if (recordId != null && recordId !== "") {
    params.set("record_id", recordId)
  }
  return `${base}?${params.toString()}`
}

/**
 * Handles image upload with progress tracking and abort capability
 * @param file The file to upload
 * @param onProgress Optional callback for tracking upload progress
 * @param abortSignal Optional AbortSignal for cancelling the upload
 * @param recordContext Optional { recordType, recordId } of the owning record
 * @returns Promise resolving to the URL of the uploaded image
 */
export const handleImageUpload = (file, onProgress, abortSignal, recordContext) => {
  if (!file) {
    return Promise.reject(new Error("No file provided"))
  }

  if (file.size > MAX_FILE_SIZE) {
    return Promise.reject(
      new Error(`File size exceeds maximum allowed (${MAX_FILE_SIZE / (1024 * 1024)}MB)`),
    )
  }

  const csrfToken =
    document.querySelector('meta[name="csrf-token"]')?.getAttribute("content") || ""

  const formData = new FormData()
  formData.append("file", file)

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.upload.addEventListener("progress", (e) => {
      if (e.lengthComputable) {
        onProgress?.({ progress: Math.round((e.loaded / e.total) * 100) })
      }
    })

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText)
          if (data.location) {
            resolve(data.location)
          } else {
            reject(new Error(data.error?.message || "Upload failed: no image URL returned"))
          }
        } catch {
          reject(new Error("Upload failed: invalid server response"))
        }
      } else {
        reject(new Error(`Upload failed (${xhr.status})`))
      }
    })

    xhr.addEventListener("error", () => reject(new Error("Upload failed: network error")))
    xhr.addEventListener("abort", () => reject(new Error("Upload cancelled")))

    if (abortSignal) {
      if (abortSignal.aborted) {
        reject(new Error("Upload cancelled"))
        return
      }
      abortSignal.addEventListener("abort", () => xhr.abort(), { once: true })
    }

    xhr.open("POST", buildImagesUploadUrl(recordContext))
    xhr.setRequestHeader("X-CSRF-Token", csrfToken)
    xhr.send(formData)
  })
}

const ATTR_WHITESPACE =
  // eslint-disable-next-line no-control-regex
  /[\u0000-\u0020\u00A0\u1680\u180E\u2000-\u2029\u205F\u3000]/g

export function isAllowedUri(
  uri,
  protocols
) {
  const allowedProtocols = [
    "http",
    "https",
    "ftp",
    "ftps",
    "mailto",
    "tel",
    "callto",
    "sms",
    "cid",
    "xmpp",
  ]

  if (protocols) {
    protocols.forEach((protocol) => {
      const nextProtocol =
        typeof protocol === "string" ? protocol : protocol.scheme

      if (nextProtocol) {
        allowedProtocols.push(nextProtocol)
      }
    })
  }

  return (!uri || uri.replace(ATTR_WHITESPACE, "").match(new RegExp(// eslint-disable-next-line no-useless-escape
  `^(?:(?:${allowedProtocols.join("|")}):|[^a-z]|[a-z0-9+.\-]+(?:[^a-z+.\-:]|$))`, "i")));
}

export function sanitizeUrl(inputUrl, baseUrl, protocols) {
  try {
    const url = new URL(inputUrl, baseUrl)

    if (isAllowedUri(url.href, protocols)) {
      return url.href
    }
  } catch {
    // If URL creation fails, it's considered invalid
  }
  return "#"
}

/**
 * Update a single attribute on multiple nodes.
 *
 * @param tr - The transaction to mutate
 * @param targets - Array of { node, pos }
 * @param attrName - Attribute key to update
 * @param next - New value OR updater function receiving previous value
 *               Pass `undefined` to remove the attribute.
 * @returns true if at least one node was updated, false otherwise
 */
export function updateNodesAttr(tr, targets, attrName, next) {
  if (!targets.length) return false

  let changed = false

  for (const { pos } of targets) {
    // Always re-read from the transaction's current doc
    const currentNode = tr.doc.nodeAt(pos)
    if (!currentNode) continue

    const prevValue = currentNode.attrs[attrName]
    const resolvedNext =
      typeof next === "function"
        ? next(prevValue)
        : next

    if (prevValue === resolvedNext) continue

    const nextAttrs = { ...currentNode.attrs }
    if (resolvedNext === undefined) {
      // Remove the key entirely instead of setting null
      delete nextAttrs[attrName]
    } else {
      nextAttrs[attrName] = resolvedNext
    }

    tr.setNodeMarkup(pos, undefined, nextAttrs)
    changed = true
  }

  return changed
}

/**
 * Selects the entire content of the current block node if the selection is empty.
 * If the selection is not empty, it does nothing.
 * @param editor The Tiptap editor instance
 */
export function selectCurrentBlockContent(editor) {
  const { selection, doc } = editor.state

  if (!selection.empty) return

  const $pos = selection.$from
  let blockNode = null
  let blockPos = -1

  for (let depth = $pos.depth; depth >= 0; depth--) {
    const node = $pos.node(depth)
    const pos = $pos.start(depth)

    if (node.isBlock && node.textContent.trim()) {
      blockNode = node
      blockPos = pos
      break
    }
  }

  if (blockNode && blockPos >= 0) {
    const from = blockPos
    const to = blockPos + blockNode.nodeSize - 2 // -2 to exclude the closing tag

    if (from < to) {
      const $from = doc.resolve(from)
      const $to = doc.resolve(to)
      const newSelection = TextSelection.between($from, $to, 1)

      if (newSelection && !selection.eq(newSelection)) {
        editor.view.dispatch(editor.state.tr.setSelection(newSelection))
      }
    }
  }
}

/**
 * Retrieves all nodes of specified types from the current selection.
 * @param selection The current editor selection
 * @param allowedNodeTypes An array of node type names to look for (e.g., ["image", "table"])
 * @returns An array of objects containing the node and its position
 */
export function getSelectedNodesOfType(selection, allowedNodeTypes) {
  const results = []
  const allowed = new Set(allowedNodeTypes)

  if (selection instanceof CellSelection) {
    selection.forEachCell((node, pos) => {
      if (allowed.has(node.type.name)) {
        results.push({ node, pos })
      }
    })
    return results
  }

  if (selection instanceof NodeSelection) {
    const { node, from: pos } = selection
    if (node && allowed.has(node.type.name)) {
      results.push({ node, pos })
    }
    return results
  }

  const { $anchor, $from, $to } = selection
  const cell = cellAround($anchor)

  if (cell) {
    const cellNode = selection.$anchor.doc.nodeAt(cell.pos)
    if (cellNode && allowed.has(cellNode.type.name)) {
      results.push({ node: cellNode, pos: cell.pos })
      return results
    }
  }

  // Selection spans multiple blocks: collect every matching block in range.
  // Ctrl/Cmd+A is an AllSelection on the document, so sameParent is true
  // even though the range covers every block — treat it like a drag-select.
  if (!selection.empty && (selection instanceof AllSelection || !$from.sameParent($to))) {
    $from.doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
      if (allowed.has(node.type.name)) {
        const contentStart = pos + 1
        const contentEnd = pos + node.nodeSize - 1
        const selectionInside = selection.from > contentStart && selection.to < contentEnd
        if (selectionInside && node.childCount > 0) return true

        results.push({ node, pos })
        return false
      }
      return true
    })
    if (results.length > 0) return results
  }

  // Fallback: find parent nodes of allowed types
  const parentNode = findParentNodeClosestToPos($anchor, (node) =>
    allowed.has(node.type.name))

  if (parentNode) {
    results.push({ node: parentNode.node, pos: parentNode.pos })
  }

  return results
}

/**
 * Clamps a value between min and max bounds
 */
export function clamp(value, min, max) {
  return Math.max(min, Math.min(value, max));
}

export function getSelectedBlockNodes(editor) {
  const { doc } = editor.state
  const { from, to } = editor.state.selection

  const blocks = []
  const seen = new Set()

  doc.nodesBetween(from, to, (node, pos) => {
    if (!node.isBlock) return

    if (!seen.has(pos)) {
      seen.add(pos)
      blocks.push(node)
    }

    return false
  })

  return blocks
}
